import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import ResetPassword from "@/pages/ResetPassword";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/ui-kit";
import AppLayout from "@/components/AppLayout";
import PortalShell from "@/components/PortalShell";
import Landing from "@/pages/Landing";
import PortalAuth from "@/pages/PortalAuth";
import Dashboard from "@/pages/Dashboard";
import Referrals from "@/pages/Referrals";
import NewReferral from "@/pages/NewReferral";
import CareEpisode from "@/pages/CareEpisode";
import Patients from "@/pages/Patients";
import Outcomes from "@/pages/Outcomes";
import Protocols from "@/pages/Protocols";
import Messages from "@/pages/Messages";
import Alerts from "@/pages/Alerts";
import Profile from "@/pages/Profile";
import ProHome from "@/pages/Pro";
import ProSettings from "@/pages/pro/ProSettings";
import ReacceptGate from "@/pages/pro/ReacceptGate";
import LegalDoc from "@/pages/LegalDoc";
import PatientHome from "@/pages/PatientHome";
import AdminHome from "@/pages/Admin";
import OpsHome from "@/pages/Ops";
import DoctorOnboarding from "@/pages/DoctorOnboarding";
import FinanceHome from "@/pages/Finance";

export const HOME = { doctor: "/dashboard", staff: "/patients", physiotherapist: "/pro", patient: "/patient", admin: "/admin", ops: "/ops", finance: "/finance", superadmin: "/admin" };

// When installed as a standalone PWA, lock the app to the portal it was installed
// from — other portals are not accessible from inside that installed app.
const LOGIN = { doctor: "/doctor/login", pro: "/pro/login", patient: "/patient/login", admin: "/admin/login", ops: "/ops/login", finance: "/finance/login" };
const DOCTOR_ROUTES = ["/login", "/dashboard", "/referrals", "/patients", "/outcomes", "/protocols", "/messages", "/alerts", "/profile", "/doctor"];
function portalOf(path) {
  if (path.startsWith("/pro")) return "pro";
  if (path === "/patient" || path.startsWith("/patient/")) return "patient";
  if (path.startsWith("/admin")) return "admin";
  if (path.startsWith("/ops")) return "ops";
  if (path.startsWith("/finance")) return "finance";
  if (path.startsWith("/patients") || path.startsWith("/doctor") || DOCTOR_ROUTES.some((r) => path === r || path.startsWith(r + "/"))) return "doctor";
  return null;
}
function PortalLock() {
  const loc = useLocation();
  const nav = useNavigate();
  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (!standalone) return;
    let locked = localStorage.getItem("pq_locked_portal");
    const cur = portalOf(loc.pathname);
    if (!locked && cur) { locked = cur; localStorage.setItem("pq_locked_portal", cur); }
    if (!locked) return;
    if (loc.pathname === "/") { nav(LOGIN[locked], { replace: true }); return; }
    if (cur && cur !== locked) nav(LOGIN[locked], { replace: true });
  }, [loc.pathname, nav]);
  return null;
}

function Loading() {
  return <div className="min-h-screen flex items-center justify-center"><Spinner className="text-teal-600 w-8 h-8" /></div>;
}

function DoctorRoute({ children }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <Loading />;
  if (!user) return <Navigate to="/doctor/login" replace />;
  if (user.role === "doctor" && user.credential_status !== "Verified") return <Navigate to="/doctor/onboarding" replace />;
  if (user.role !== "doctor" && user.role !== "staff" && user.role !== "superadmin") return <Navigate to={HOME[user.role] || "/"} replace />;
  return <AppLayout>{children}</AppLayout>;
}

function DoctorOnboardingRoute() {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <Loading />;
  if (!user) return <Navigate to="/doctor/login" replace />;
  if (user.role !== "doctor") return <Navigate to={HOME[user.role] || "/"} replace />;
  if (user.credential_status === "Verified") return <Navigate to="/dashboard" replace />;
  return <DoctorOnboarding />;
}

function PortalRoute({ role, portal, children }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <Loading />;
  if (!user) return <Navigate to={`/${portal}/login`} replace />;
  if (user.role !== role && user.role !== "superadmin") return <Navigate to={HOME[user.role] || "/"} replace />;
  return <PortalShell portal={portal}>{children}</PortalShell>;
}

const D = (el) => <DoctorRoute>{el}</DoctorRoute>;

function FinanceRoute() {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <Loading />;
  if (!user) return <Navigate to="/finance/login" replace />;
  if (user.role !== "finance" && user.role !== "admin" && user.role !== "superadmin") return <Navigate to={HOME[user.role] || "/"} replace />;
  return <FinanceHome />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="bottom-right" richColors closeButton />
        <PortalLock />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<PortalAuth portal="doctor" />} />
          <Route path="/doctor/login" element={<PortalAuth portal="doctor" />} />
          <Route path="/doctor/onboarding" element={<DoctorOnboardingRoute />} />
          <Route path="/reset" element={<ResetPassword />} />
          <Route path="/pro/login" element={<PortalAuth portal="pro" />} />
          <Route path="/patient/login" element={<PortalAuth portal="patient" />} />
          <Route path="/admin/login" element={<PortalAuth portal="admin" />} />
          <Route path="/ops/login" element={<PortalAuth portal="ops" />} />
          <Route path="/finance/login" element={<PortalAuth portal="finance" />} />
          <Route path="/finance" element={<FinanceRoute />} />

          {/* Doctor (desktop) */}
          <Route path="/dashboard" element={D(<Dashboard />)} />
          <Route path="/referrals" element={D(<Referrals />)} />
          <Route path="/referrals/new" element={D(<NewReferral />)} />
          <Route path="/referrals/:id" element={D(<CareEpisode />)} />
          <Route path="/patients" element={D(<Patients />)} />
          <Route path="/outcomes" element={D(<Outcomes />)} />
          <Route path="/protocols" element={D(<Protocols />)} />
          <Route path="/messages" element={D(<Messages />)} />
          <Route path="/alerts" element={D(<Alerts />)} />
          <Route path="/profile" element={D(<Profile />)} />

          {/* PranIQ Pro (mobile) */}
          <Route path="/pro" element={<PortalRoute role="physiotherapist" portal="pro"><ReacceptGate><ProHome /></ReacceptGate></PortalRoute>} />
          <Route path="/pro/settings" element={<PortalRoute role="physiotherapist" portal="pro"><ProSettings /></PortalRoute>} />
          <Route path="/legal/clinician/:doc" element={<LegalDoc />} />
          {/* Patient (mobile) */}
          <Route path="/patient" element={<PortalRoute role="patient" portal="patient"><PatientHome /></PortalRoute>} />
          {/* Admin (desktop) */}
          <Route path="/admin" element={<PortalRoute role="admin" portal="admin"><AdminHome /></PortalRoute>} />
          {/* PranIQ Operations (desktop) */}
          <Route path="/ops" element={<PortalRoute role="ops" portal="ops"><OpsHome /></PortalRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
